/* eslint-disable prettier/prettier */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormControl,
  FormLabel,
  Flex,
  Grid,
  GridItem,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import Cookies from 'js-cookie';

type FormProps = {
  formUrl: string;
  isOpen: boolean;
  onClose: () => void;
};

const Form = ({ formUrl, isOpen, onClose }: FormProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    const data = new FormData(event.currentTarget);
    const object: Record<string, any> = {};
    data.forEach((value, key) => {
      object[key] = value;
    });

    const referralCodes = Cookies.get('camp');
    object.origin = referralCodes;

    const body = JSON.stringify(object);

    fetch(formUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body,
    })
      .then(() => {
        setLoading(false);
        toast({
          title: t('form.success.title', 'Muchas gracias por contactarnos.'),
          description: t('form.success.description', 'En breve nos comunicaremos contigo.'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        Cookies.remove('camp');
        onClose();
      })
      .catch(() => {
        setLoading(false);
        toast({
          title: t('form.error.title', 'Parece que algo salió mal.'),
          description: t('form.error.description', 'Por favor, intente más tarde.'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxW="665px">
        <form onSubmit={handleSubmit}>
          <ModalHeader>{t('form.title', 'Formulario de contacto')}</ModalHeader>
          <ModalBody>
            <Text mb={8}>
              {t(
                'form.subtitle',
                'Dejanos tus datos para que podamos ayudarte en lo que necesites, o contarte más acerca de esta nueva dimensión de los negocios agropecuarios:'
              )}
            </Text>
            <Grid templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(2, 1fr)' }} gap={6}>
              <GridItem w="100%">
                <FormControl>
                  <FormLabel>{t('form.name', 'Nombre')}</FormLabel>
                  <Input required name="nombre" placeholder="Ingresá tu nombre" />
                </FormControl>
              </GridItem>

              <GridItem w="100%">
                <FormControl>
                  <FormLabel>{t('form.lastname', 'Apellido')}</FormLabel>
                  <Input required name="apellido" placeholder="Ingresa tu apellido" />
                </FormControl>
              </GridItem>

              <GridItem w="100%">
                <FormControl>
                  <FormLabel>{t('form.phone', 'Celular')}</FormLabel>
                  <Input
                    required
                    name="phone"
                    type="tel"
                    placeholder="Ingresá tu celular (no incluir 0 ni 15)"
                  />
                </FormControl>
              </GridItem>

              <GridItem w="100%">
                <FormControl>
                  <FormLabel>{t('form.email', 'Correo electrónico')}</FormLabel>
                  <Input
                    required
                    name="email"
                    type="email"
                    placeholder="Ingresá tu correo electrónico"
                  />
                </FormControl>
              </GridItem>

              <GridItem w="100%">
                <FormControl mb={{ base: 0, lg: 6 }}>
                  <FormLabel>{t('form.role', 'Motivo de la consulta')}</FormLabel>
                  <Select name="role" defaultValue="" required>
                    <option value="" disabled>
                      {t('form.reason')}
                    </option>
                    <option value="inversor">{t('form.inversor')}</option>
                    <option value="propietario">{t('form.propietario')}</option>
                    <option value="operador">{t('form.operador')}</option>
                    <option value="inmobiliaria">{t('form.inmobiliaria')}</option>
                    <option value="finan_advisor">{t('form.finan_advisor')}</option>
                    <option value="otro">{t('form.otro')}</option>
                  </Select>
                </FormControl>
              </GridItem>

              <GridItem w="100%">
                <FormControl mb={{ base: 6, lg: 0 }}>
                  <FormLabel>{t('form.company', 'Empresa')}</FormLabel>
                  <Input required name="company" placeholder="Ingresá tu empresa" />
                </FormControl>
              </GridItem>
            </Grid>

            <FormControl mb={4}>
              <FormLabel>{t('form.message', 'Mensaje')}</FormLabel>
              <Textarea name="message" placeholder="Escribí tu mensaje" />
            </FormControl>
          </ModalBody>
          {!loading && (
            <ModalFooter justifyContent="flex-start">
              <Button variant="primary" mr={3} type="submit">
                {t('submit', 'Enviar')}
              </Button>
              <Button variant="outline" onClick={onClose} fontWeight="400">
                {t('cancel', 'Tal vez luego')}
              </Button>
            </ModalFooter>
          )}
        </form>
        {loading && (
          <Flex justify="center" py={8}>
            <Spinner mr={6} />
            <Text color="gray.700">{t('form.loading')}</Text>
          </Flex>
        )}
      </ModalContent>
    </Modal>
  );
};
export default Form;
